import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import AppLayout from "../../lib/components/Layout/app-layout";
import pathnames from "../../constants/pathnames";
import promoCodeService from "../../service/promoCodeService";
import customPlanService from "../../service/customPlanService";
import organizationService from "../../service/organizationservice";
import useDebounce from "../../hooks/useDebounce";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ArrowBack, AutoFixHighOutlined, Close, Search } from "@mui/icons-material";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import BTNLoader from "../../lib/components/BTNLoader";
import PromoStatusBadge from "./components/promo-status-badge";
import "./promo-codes.scss";

const RETIRED_DISCOUNT_TYPES = ["FREE_SETUP", "FREE_TRIAL"];
const BASE_PLAN_NAMES = ["simple start", "standard", "premium", "enterprise"];

const perCustomerLimitToOption = (limit) => {
  if (limit === null) return "UNLIMITED";
  if (limit === undefined) return "1";
  return String(limit);
};

const optionToPerCustomerLimit = (option) => (option === "UNLIMITED" ? null : Number(option));

export default function CreatePromoCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get("id");
  const isEditMode = Boolean(editId);

  // Fetch existing code data when in edit mode
  const { data: existingCode } = useQuery({
    queryKey: ["promo-code-single", editId],
    queryFn: () => promoCodeService.getPromoCode(editId),
    enabled: isEditMode,
  });

  // Wizard stage: 1 (Steps 1-2), 2 (Steps 3-4), 3 (Steps 5-6)
  const [currentStage, setCurrentStage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  // Step 1: Basic Information
  const [promoCode, setPromoCode] = useState("");
  const [description, setDescription] = useState("");

  // Step 2: Discount Configuration
  const [discountType, setDiscountType] = useState("PERCENTAGE"); // PERCENTAGE | FIXED | FIRST_MONTH_FREE | FREE_SETUP | FREE_TRIAL | CUSTOM
  // eslint-disable-next-line no-unused-vars
  const [customAmountType, setCustomAmountType] = useState("PERCENTAGE"); // PERCENTAGE | FIXED (for CUSTOM discountType)
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [maxDiscountCap, setMaxDiscountCap] = useState("");

  // Step 3: Plan Eligibility (Fetched dynamically from customPlanService)
  const { data: plansResponse, isError: isPlansError, isLoading: isPlansLoading } = useQuery({
    queryKey: ["promo-code-available-plans"],
    queryFn: () => customPlanService.getPlans(),
    staleTime: 10 * 60 * 1000, // 10 minutes cache to avoid refetching on step transitions
    refetchOnWindowFocus: false,
  });

  const availablePlans = React.useMemo(() => {
    const raw = plansResponse?.data?.data || [];
    if (!Array.isArray(raw)) return [];
    const filtered = raw.filter(
      (p) =>
        !p.deleted &&
        (p.type || "DEFAULT") === "DEFAULT" &&
        BASE_PLAN_NAMES.includes((p.name || "").toLowerCase().replace(/[\s\-_]+/g, " ").trim())
    );
    return filtered
      .map((p) => ({
        ...p,
        price: p.pricing?.monthly?.amount
          ? `₦${Number(p.pricing.monthly.amount).toLocaleString()}/mo`
          : "Standard Tier",
      }))
      .sort((a, b) => (a.rank || 0) - (b.rank || 0));
  }, [plansResponse]);

  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const hasInitializedPlans = React.useRef(false);

  // Auto-populate all standard plans selected by default once availablePlans is loaded (create mode only)
  React.useEffect(() => {
    if (!isEditMode && availablePlans.length > 0 && !hasInitializedPlans.current) {
      setSelectedPlanIds(availablePlans.map((p) => p._id || p.id));
      hasInitializedPlans.current = true;
    }
  }, [availablePlans, isEditMode]);

  // Step 4: Customer Eligibility
  const [customerEligibility, setCustomerEligibility] = useState("PUBLIC"); // PUBLIC | NEW_ONLY | EXISTING_ONLY | PARTNER_REFERRAL | COHORT | DOMAINS
  const [eligibleDomains, setEligibleDomains] = useState([]);
  const [domainInput, setDomainInput] = useState("");
  const [isDomainFocused, setIsDomainFocused] = useState(false);
  const domainInputRef = React.useRef(null);
  const [selectedCohortOrgs, setSelectedCohortOrgs] = useState([]); // [{ id, name, email, plan, status }]
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const debouncedOrgSearch = useDebounce(orgSearchTerm, 350);
  const [cohortId, setCohortId] = useState("");
  const [manualOrgIdInput, setManualOrgIdInput] = useState("");
  const [showManualOrgInput, setShowManualOrgInput] = useState(false);
  const [minSpend, setMinSpend] = useState("");
  const [maxSpend, setMaxSpend] = useState("");
  const [minCommitment, setMinCommitment] = useState("No minimum");

  // Domain chip management helpers
  const cleanDomain = (raw) => {
    return (raw || "")
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .replace(/^@/, "")
      .replace(/\/.*$/, "");
  };

  const handleAddDomain = (rawDomain) => {
    if (!rawDomain) return;
    const tokens = rawDomain
      .split(/[\s,;]+/)
      .map(cleanDomain)
      .filter((d) => d && d.length >= 2);

    if (tokens.length === 0) return;

    setEligibleDomains((prev) => {
      const next = [...prev];
      tokens.forEach((d) => {
        if (!next.includes(d)) {
          next.push(d);
        }
      });
      return next;
    });
    setDomainInput("");
  };

  const handleDomainInputChange = (e) => {
    const val = e.target.value;
    if (val.includes(",") || val.includes(";") || (val.endsWith(" ") && val.trim().length >= 2)) {
      handleAddDomain(val);
    } else {
      setDomainInput(val);
    }
  };

  const handleDomainKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (domainInput.trim()) {
        handleAddDomain(domainInput);
      }
    } else if (e.key === "Backspace" && !domainInput && eligibleDomains.length > 0) {
      setEligibleDomains((prev) => prev.slice(0, prev.length - 1));
    }
  };

  const handleDomainBlur = () => {
    if (domainInput.trim()) {
      handleAddDomain(domainInput);
    }
    setIsDomainFocused(false);
  };

  const handleDomainPaste = (e) => {
    const pasted = e.clipboardData?.getData("text");
    if (
      pasted &&
      (pasted.includes(",") ||
        pasted.includes(" ") ||
        pasted.includes("\n") ||
        pasted.includes(";"))
    ) {
      e.preventDefault();
      handleAddDomain(pasted);
    }
  };

  const handleRemoveDomain = (domainToRemove) => {
    setEligibleDomains((prev) => prev.filter((d) => d !== domainToRemove));
  };

  const handleClearAllDomains = () => {
    setEligibleDomains([]);
    setDomainInput("");
  };

  // Query organizations when COHORT eligibility is selected
  const { data: cohortOrgsResponse, isFetching: isSearchingCohortOrgs } = useQuery({
    queryKey: ["cohort-organizations", debouncedOrgSearch],
    queryFn: () => organizationService.get({ keyword: debouncedOrgSearch, limit: 50 }),
    enabled: customerEligibility === "COHORT",
    staleTime: 60 * 1000,
  });

  const availableCohortOrgs = React.useMemo(() => {
    const res = cohortOrgsResponse?.data?.data;
    const list = res?.docs || res?.data || (Array.isArray(res) ? res : []);
    if (!Array.isArray(list)) return [];
    return list.map((org) => {
      const id = String(org._id || org.id || "");
      const name = org.name || org.organizationName || org.email || id;
      const email = org.email || "";
      const plan = org.subscription?.planName || org.plan || "";
      const status =
        org.subscription?.status || org.subscriptionStatus || (org.isActive ? "ACTIVE" : "INACTIVE");
      return { id, name, email, plan, status };
    });
  }, [cohortOrgsResponse]);

  const handleToggleCohortOrg = (org) => {
    setSelectedCohortOrgs((prev) => {
      const exists = prev.some((o) => o.id === org.id);
      if (exists) {
        return prev.filter((o) => o.id !== org.id);
      } else {
        return [...prev, org];
      }
    });
  };

  const handleRemoveCohortOrg = (orgId) => {
    setSelectedCohortOrgs((prev) => prev.filter((o) => o.id !== orgId));
  };

  const handleAddManualOrgId = () => {
    const trimmed = manualOrgIdInput.trim();
    if (!trimmed) return;
    if (!selectedCohortOrgs.some((o) => o.id === trimmed)) {
      setSelectedCohortOrgs((prev) => [
        ...prev,
        { id: trimmed, name: trimmed, email: "Manual entry", status: "CUSTOM" },
      ]);
    }
    setManualOrgIdInput("");
  };

  // Step 5: Usage Limits
  const [totalRedemptionLimit, setTotalRedemptionLimit] = useState("");
  const [limitPerCustomer, setLimitPerCustomer] = useState("1");
  const [maxPerDay, setMaxPerDay] = useState("");

  // Step 6: Validity & Scheduling
  const [startDate, setStartDate] = useState(() => new Date());
  const [startTime, setStartTime] = useState(() => new Date());
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    d.setHours(23, 59, 0, 0);
    return d;
  });
  const [expiryTime, setExpiryTime] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d;
  });
  const [neverExpires, setNeverExpires] = useState(false);

  // Pre-populate form state in Edit Mode
  const hasPopulatedExisting = React.useRef(false);
  React.useEffect(() => {
    if (existingCode && !hasPopulatedExisting.current) {
      if (existingCode.code) setPromoCode(existingCode.code);
      if (existingCode.description) setDescription(existingCode.description);
      if (existingCode.discountType) setDiscountType(existingCode.discountType.toUpperCase());
      if (existingCode.amountType) setCustomAmountType(existingCode.amountType.toUpperCase());
      if (existingCode.amount !== undefined && existingCode.amount !== null) {
        setDiscountPercentage(String(existingCode.amount));
      }
      if (existingCode.maxDiscountAmount) {
        setMaxDiscountCap(String(existingCode.maxDiscountAmount));
      }

      // Plans
      if (existingCode.planSelect === "CUSTOM" && Array.isArray(existingCode.plans)) {
        setSelectedPlanIds(existingCode.plans);
      } else if (availablePlans.length > 0) {
        setSelectedPlanIds(availablePlans.map((p) => p._id || p.id));
      }

      // Eligibility
      if (existingCode.customerEligibility) {
        setCustomerEligibility(existingCode.customerEligibility);
      }
      if (Array.isArray(existingCode.eligibleDomains) && existingCode.eligibleDomains.length > 0) {
        setEligibleDomains(existingCode.eligibleDomains);
      }
      if (existingCode.cohortId) setCohortId(existingCode.cohortId);
      if (Array.isArray(existingCode.cohortMemberOrgIds) && existingCode.cohortMemberOrgIds.length > 0) {
        setSelectedCohortOrgs(
          existingCode.cohortMemberOrgIds.map((id) => ({
            id,
            name: id,
            email: "",
            plan: "",
            status: "ACTIVE",
          }))
        );
      }
      if (existingCode.minSpend) setMinSpend(String(existingCode.minSpend));
      if (existingCode.maxSpend) setMaxSpend(String(existingCode.maxSpend));
      if (existingCode.minCommitment) setMinCommitment(existingCode.minCommitment);

      // Usage limits
      if (existingCode.usage?.totalLimit) {
        setTotalRedemptionLimit(String(existingCode.usage.totalLimit));
      }
      if (existingCode.usage) {
        setLimitPerCustomer(perCustomerLimitToOption(existingCode.usage.perCustomerLimit));
      }
      if (existingCode.usage?.perDayLimit) {
        setMaxPerDay(String(existingCode.usage.perDayLimit));
      }

      // Schedule & Dates
      if (existingCode.schedule?.startAt) {
        const s = new Date(existingCode.schedule.startAt);
        if (!isNaN(s.getTime())) {
          setStartDate(s);
          setStartTime(s);
        }
      }
      if (existingCode.schedule?.expiryAt) {
        const e = new Date(existingCode.schedule.expiryAt);
        if (!isNaN(e.getTime())) {
          setExpiryDate(e);
          setExpiryTime(e);
        }
      }
      if (existingCode.schedule?.neverExpires !== undefined) {
        setNeverExpires(Boolean(existingCode.schedule.neverExpires));
      }

      hasPopulatedExisting.current = true;
    }
  }, [existingCode, availablePlans]);

  const combineDateAndTime = (dateObj, timeObj) => {
    if (!dateObj) return new Date();
    const d = new Date(dateObj);
    if (timeObj) {
      const t = new Date(timeObj);
      d.setHours(t.getHours(), t.getMinutes(), t.getSeconds() || 0, 0);
    }
    return d;
  };

  // Auto-generate promo code
  const handleAutoGenerate = async () => {
    try {
      const code = await promoCodeService.generateCode("JUREB");
      setPromoCode(code);
      toast.info(`Generated code: ${code}`);
    } catch (e) {
      const randomSuffix = Math.floor(1000 + Math.random() * 9000);
      setPromoCode(`JUREB${randomSuffix}`);
      toast.info("Generated new code!");
    }
  };

  // Plan selection toggling
  const handleTogglePlan = (planId) => {
    setSelectedPlanIds((prev) =>
      prev.includes(planId)
        ? prev.filter((p) => p !== planId)
        : [...prev, planId]
    );
  };

  const handleSelectAllPlans = () => {
    if (selectedPlanIds.length === availablePlans.length) {
      setSelectedPlanIds([]);
    } else {
      setSelectedPlanIds(availablePlans.map((p) => p._id || p.id));
    }
  };

  // Stage-by-stage validation helper
  const validateStage = (stage) => {
    if (stage === 1) {
      if (!promoCode.trim()) {
        toast.error("Please enter or auto-generate a promo code");
        return false;
      }
      if (RETIRED_DISCOUNT_TYPES.includes(discountType)) {
        toast.error("Free Setup and Free Trial codes are no longer supported. Choose another discount type to save.");
        return false;
      }
      if (discountType === "CUSTOM" && !description.trim()) {
        toast.error("Please add a description for a Custom Offer so customers know what it is");
        return false;
      }
      if (
        (discountType === "PERCENTAGE" || discountType === "FIXED" || discountType === "CUSTOM") &&
        (!discountPercentage || Number(discountPercentage) <= 0)
      ) {
        toast.error(
          discountType === "FIXED" || (discountType === "CUSTOM" && customAmountType === "FIXED")
            ? "Please enter a valid discount amount (e.g. ₦50,000)"
            : "Please enter a valid discount percentage (e.g. 20%)"
        );
        return false;
      }
      if (
        (discountType === "PERCENTAGE" || (discountType === "CUSTOM" && customAmountType === "PERCENTAGE")) &&
        Number(discountPercentage) > 100
      ) {
        toast.error("Percentage discount cannot exceed 100%");
        return false;
      }
      return true;
    }

    if (stage === 2) {
      if (isPlansError || (!isPlansLoading && availablePlans.length === 0)) {
        toast.error("Could not load subscription plans. Refresh the page and try again.");
        return false;
      }
      if (isPlansLoading) {
        toast.error("Subscription plans are still loading. Please wait a moment.");
        return false;
      }
      if (minSpend && maxSpend && Number(minSpend) > Number(maxSpend)) {
        toast.error("Minimum spend cannot be more than maximum spend");
        return false;
      }
      if (selectedPlanIds.length === 0 && availablePlans.length > 0) {
        toast.error("Please select at least one applicable subscription plan");
        return false;
      }

      let finalDomains = [...eligibleDomains];
      if (domainInput.trim()) {
        const cleaned = cleanDomain(domainInput);
        if (cleaned && !finalDomains.includes(cleaned)) {
          finalDomains.push(cleaned);
        }
      }

      if (customerEligibility === "DOMAINS" && finalDomains.length === 0) {
        toast.error("Please provide at least one eligible corporate domain");
        return false;
      }

      if (customerEligibility === "COHORT" && selectedCohortOrgs.length === 0) {
        toast.error("Please select at least one Organization for the cohort membership");
        return false;
      }

      return true;
    }

    if (stage === 3) {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      if (!isEditMode && startDate && new Date(startDate).getTime() < todayStart.getTime()) {
        toast.error("Start date cannot be in the past. Please select today or a future date.");
        return false;
      }

      if (!neverExpires) {
        const start = combineDateAndTime(startDate, startTime);
        const expiry = combineDateAndTime(expiryDate, expiryTime);
        if (expiry <= start) {
          toast.error("Expiry date & time must be after the start date & time");
          return false;
        }
      }
      return true;
    }

    return true;
  };

  // Form submission
  const handleSubmit = async () => {
    if (!validateStage(1)) {
      setCurrentStage(1);
      return;
    }
    if (!validateStage(2)) {
      setCurrentStage(2);
      return;
    }
    if (!validateStage(3)) {
      return;
    }

    let finalDomains = [...eligibleDomains];
    if (domainInput.trim()) {
      const cleaned = cleanDomain(domainInput);
      if (cleaned && !finalDomains.includes(cleaned)) {
        finalDomains.push(cleaned);
      }
    }

    setIsSubmitting(true);
    try {
      const isAllPlansSelected =
        selectedPlanIds.length === availablePlans.length || selectedPlanIds.length === 0;

      const domainsList =
        customerEligibility === "DOMAINS" ? finalDomains : [];

      const cohortList =
        customerEligibility === "COHORT"
          ? selectedCohortOrgs.map((org) => org.id).filter(Boolean)
          : [];

      // Determine amount and amountType strictly according to backend guide
      let calculatedAmount = Number(discountPercentage) || 0;
      let calculatedAmountType = "PERCENTAGE";

      if (discountType === "PERCENTAGE") {
        calculatedAmountType = "PERCENTAGE";
        calculatedAmount = Number(discountPercentage) || 0;
      } else if (discountType === "FIXED") {
        calculatedAmountType = "FIXED";
        calculatedAmount = Number(discountPercentage) || 0;
      } else if (
        discountType === "FIRST_MONTH_FREE" ||
        discountType === "FREE_SETUP" ||
        discountType === "FREE_TRIAL"
      ) {
        calculatedAmountType = "PERCENTAGE";
        calculatedAmount = 100;
      } else if (discountType === "CUSTOM") {
        calculatedAmountType = customAmountType; // "PERCENTAGE" or "FIXED"
        calculatedAmount = Number(discountPercentage) || 0;
      }

      const payload = {
        code: promoCode.toUpperCase().trim(),
        description: description.trim(),
        discountType: discountType.toUpperCase(),
        amount: calculatedAmount,
        amountType: calculatedAmountType,
        maxDiscountAmount: maxDiscountCap ? Number(maxDiscountCap) : null,
        planSelect: isAllPlansSelected ? "ALL" : "CUSTOM",
        plans: isAllPlansSelected ? [] : selectedPlanIds,
        customerEligibility,
        eligibleDomains: domainsList,
        cohortMemberOrgIds: cohortList,
        ...(cohortId.trim() ? { cohortId: cohortId.trim() } : {}),
        minSpend: minSpend ? Number(minSpend) : null,
        maxSpend: maxSpend ? Number(maxSpend) : null,
        minCommitment: minCommitment === "No minimum" ? null : minCommitment,
        usage: {
          totalLimit: totalRedemptionLimit ? Number(totalRedemptionLimit) : null,
          perCustomerLimit: optionToPerCustomerLimit(limitPerCustomer),
          perDayLimit: maxPerDay ? Number(maxPerDay) : null,
        },
        schedule: {
          startAt: combineDateAndTime(startDate, startTime).toISOString(),
          expiryAt: neverExpires
            ? null
            : combineDateAndTime(expiryDate, expiryTime).toISOString(),
          neverExpires,
        },
      };

      if (isEditMode) {
        await promoCodeService.updatePromoCode(editId, payload);
        toast.success(`Promo code "${payload.code}" updated successfully!`);
      } else {
        await promoCodeService.createPromoCode(payload);
        toast.success(`Promo code "${payload.code}" published successfully!`);
      }
      navigate(pathnames.PROMO_CODES_OVERVIEW);
    } catch (e) {
      errorHandler(e?.response || e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLockedDiscount =
    discountType === "FIRST_MONTH_FREE" || discountType === "FREE_SETUP" || discountType === "FREE_TRIAL";
  const isFixedAmount = discountType === "FIXED" || (discountType === "CUSTOM" && customAmountType === "FIXED");
  const isPercentAmount =
    discountType === "PERCENTAGE" || (discountType === "CUSTOM" && customAmountType === "PERCENTAGE");

  const renderStepTitle = (step, title) => (
    <div className="d-flex align-items-center gap-2 mb-3">
      <span className="step-badge">{step}</span>
      <div className="fs-5 fw-medium">{title}</div>
    </div>
  );

  const renderDateTimeField = ({ label, required, date, onDateChange, time, onTimeChange, minDate, disabled }) => (
    <div className="col-12 col-sm-6">
      <label className="form-label-custom">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <div className="d-flex gap-2">
        <div style={{ flex: 1 }}>
          <DesktopDatePicker
            inputFormat="dd/MM/yyyy"
            value={date}
            minDate={minDate}
            disabled={disabled}
            onChange={onDateChange}
            renderInput={(params) => <TextField fullWidth size="small" className="bg-white" {...params} />}
          />
        </div>
        <div style={{ width: 150 }}>
          <TimePicker
            value={time}
            disabled={disabled}
            onChange={onTimeChange}
            renderInput={(params) => <TextField fullWidth size="small" className="bg-white" {...params} />}
          />
        </div>
      </div>
    </div>
  );

  return (
    <AppLayout>
      <Header
        title={isEditMode ? "Edit Promo Code" : "Create Promo Code"}
        description={
          isEditMode
            ? "Update discount rules, customer eligibility and redemption constraints"
            : "Configure discount rules, customer eligibility and redemption constraints"
        }
      />
      <div className="p-3 bg-light promo-codes-form" style={{ minHeight: "100%" }}>
        <IconButton className="bg-secondary-subtle mb-3" onClick={() => navigate(pathnames.PROMO_CODES_OVERVIEW)}>
          <ArrowBack />
        </IconButton>

        <Paper className="mx-auto" style={{ maxWidth: 960 }}>
          <div className="p-3 border-bottom d-flex justify-content-between align-items-center gap-3">
            <div>
              <div className="fs-5 fw-medium">{isEditMode ? `Edit ${promoCode || "Promo Code"}` : "New Promo Code"}</div>
              <div className="fs-sm text-secondary">Stage {currentStage} of 3</div>
            </div>
          </div>

          <div className="p-3 p-lg-4">
            {/* ================= STAGE 1: Steps 1 & 2 ================= */}
            {currentStage === 1 && (
              <div>
                {/* Step 1: Basic Information */}
                <div className="mb-4">
                  {renderStepTitle(1, "Basic Information")}

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label-custom mb-0">
                        Promo Code <span className="text-danger">*</span>
                      </label>
                      <Button size="small" color="secondary" startIcon={<AutoFixHighOutlined />} onClick={handleAutoGenerate}>
                        Auto-generate
                      </Button>
                    </div>
                    <AppTextField
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="e.g. SUMMER26 or JUREB50"
                      helperText="Codes are automatically capitalized."
                    />
                  </div>

                  <div>
                    <label className="form-label-custom">
                      Internal Description <span className="text-secondary">(Admin &amp; Finance audit purpose)</span>
                    </label>
                    <AppTextField
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="e.g. 20% discount on annual subscriptions for Q4 campaign"
                    />
                  </div>
                </div>

                <Divider className="my-4" />

                {/* Step 2: Discount Configuration */}
                <div>
                  {renderStepTitle(2, "Discount Configuration")}

                  <div className="mb-3">
                    <label className="form-label-custom">Discount Type</label>
                    <div className="row g-2">
                      {[
                        { id: "PERCENTAGE", title: "Percentage", subtext: "e.g. 20% off" },
                        { id: "FIXED", title: "Fixed Amount", subtext: "e.g. ₦50,000 off" },
                        { id: "FIRST_MONTH_FREE", title: "First Month Free", subtext: "100% off month 1" },
                        { id: "CUSTOM", title: "Custom Offer", subtext: "Bespoke rule" },
                      ].map((item) => (
                        <div key={item.id} className="col-12 col-sm-6 col-md-3">
                          <div
                            onClick={() => setDiscountType(item.id)}
                            className={`selectable-card h-100 ${discountType === item.id ? "selected" : ""}`}
                          >
                            <div className="card-title">{item.title}</div>
                            <div className="card-subtext">{item.subtext}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {RETIRED_DISCOUNT_TYPES.includes(discountType) && (
                    <Alert severity="warning" className="mb-3">
                      This code uses a discount type that is no longer supported (
                      {discountType === "FREE_SETUP" ? "Free Setup" : "Free Trial"}). Choose another discount type
                      above to save changes.
                    </Alert>
                  )}

                  {isLockedDiscount && (
                    <Alert severity="info" className="mb-3">
                      <div className="fw-medium mb-1">
                        {discountType === "FIRST_MONTH_FREE" && "First Month 100% Free"}
                        {discountType === "FREE_SETUP" && "Setup Fee Waived (100%)"}
                        {discountType === "FREE_TRIAL" && "Extended Free Trial"}
                      </div>
                      {discountType === "FIRST_MONTH_FREE" &&
                        "Waives 100% of Month 1 subscription billing at checkout. Subsequent renewal cycles automatically bill at standard plan pricing. Card binding requires a small settlement token."}
                      {discountType === "FREE_SETUP" &&
                        "Waives 100% of the one-time implementation/setup fee at checkout. Recurring subscription plan fees remain at standard pricing."}
                      {discountType === "FREE_TRIAL" &&
                        "Grants customers full trial access to selected plans without immediate billing. Recurring payment starts once the trial duration concludes."}
                    </Alert>
                  )}

                  {discountType === "CUSTOM" && (
                    <div className="mb-3 p-3 border rounded bg-light d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                      <div>
                        <div className="fw-medium">Bespoke Custom Offer</div>
                        <div className="fs-sm text-secondary">
                          Select whether this bespoke rule applies as a percentage discount or a flat Naira deduction.
                        </div>
                      </div>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        className="bg-white flex-shrink-0"
                        value={customAmountType}
                        onChange={(_, value) => value && setCustomAmountType(value)}
                      >
                        <ToggleButton value="PERCENTAGE" sx={{ textTransform: "none" }}>
                          % Percentage
                        </ToggleButton>
                        <ToggleButton value="FIXED" sx={{ textTransform: "none" }}>
                          ₦ Fixed Amount
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <label className="form-label-custom">
                        {isFixedAmount
                          ? "Discount Amount (₦)"
                          : discountType === "FIRST_MONTH_FREE"
                          ? "First Month Discount Rate"
                          : discountType === "FREE_SETUP"
                          ? "Setup Fee Waiver Rate"
                          : discountType === "FREE_TRIAL"
                          ? "Trial Discount Rate"
                          : "Percentage (%)"}
                      </label>
                      <AppTextField
                        type={isLockedDiscount ? "text" : "number"}
                        disabled={isLockedDiscount}
                        value={
                          isLockedDiscount
                            ? discountType === "FIRST_MONTH_FREE"
                              ? "100% (Month 1 Waived)"
                              : discountType === "FREE_SETUP"
                              ? "100% (Setup Waived)"
                              : "100% (During Trial)"
                            : discountPercentage
                        }
                        onChange={(e) => setDiscountPercentage(e.target.value)}
                        placeholder={isFixedAmount ? "e.g. 50000" : "e.g. 20"}
                        inputProps={{ min: 1, max: isPercentAmount ? 100 : undefined }}
                        helperText={
                          (discountType === "PERCENTAGE" && "Discount percentage applied to the chosen plan at checkout.") ||
                          (discountType === "FIXED" && "Flat Naira cash deduction applied to the checkout invoice total.") ||
                          (discountType === "CUSTOM" &&
                            (customAmountType === "FIXED"
                              ? "Flat bespoke Naira deduction under custom terms."
                              : "Bespoke percentage deducted from plan price.")) ||
                          (discountType === "FIRST_MONTH_FREE" &&
                            "Automatically sets 100% discount on month 1 in the checkout payload.") ||
                          (discountType === "FREE_SETUP" && "Automatically sets 100% discount for setup fee waiver.") ||
                          (discountType === "FREE_TRIAL" && "Automatically grants 100% discount during the trial period.") ||
                          undefined
                        }
                      />
                    </div>

                    <div className="col-12 col-sm-6">
                      <label className="form-label-custom">
                        Maximum Discount Amount <span className="text-secondary">(₦ Cap, optional)</span>
                      </label>
                      <AppTextField
                        type="number"
                        value={maxDiscountCap}
                        onChange={(e) => setMaxDiscountCap(e.target.value)}
                        placeholder="Optional cap (e.g. 100000)"
                        helperText={
                          isFixedAmount
                            ? "Optional safety ceiling. If left blank, the full fixed discount applies without a cap."
                            : discountType === "FIRST_MONTH_FREE"
                            ? "Optional cap to limit the maximum Month 1 subsidy value on high-tier plans."
                            : "Caps maximum discount granted, e.g. 20% capped at ₦100,000 max."
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= STAGE 2: Steps 3 & 4 ================= */}
            {currentStage === 2 && (
              <div>
                {/* Step 3: Plan Eligibility */}
                <div className="mb-4">
                  {renderStepTitle(3, "Plan Eligibility")}

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label-custom mb-0">Eligible Subscription Plans</label>
                    <Button size="small" color="secondary" onClick={handleSelectAllPlans}>
                      {selectedPlanIds.length === availablePlans.length ? "Deselect All Plans" : "Select All Plans"}
                    </Button>
                  </div>

                  <div className="row g-2">
                    {availablePlans.map((plan) => {
                      const planKey = plan._id || plan.id;
                      const isSelected = selectedPlanIds.includes(planKey);
                      return (
                        <div key={planKey} className="col-12 col-sm-6 col-md-3">
                          <div
                            onClick={() => handleTogglePlan(planKey)}
                            className={`selectable-card h-100 text-center ${isSelected ? "selected" : ""}`}
                          >
                            <div className="card-title">{plan.name}</div>
                            <div className="card-subtext">{plan.price || "Standard Tier"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Divider className="my-4" />

                {/* Step 4: Customer Eligibility */}
                <div>
                  {renderStepTitle(4, "Customer Eligibility")}

                  <div className="mb-3">
                    <label className="form-label-custom">Who can use this promo code?</label>
                    <div className="row g-2">
                      {[
                        { id: "PUBLIC", title: "Everyone (Public)", subtext: "All customers and prospects" },
                        { id: "NEW_ONLY", title: "New Customers Only", subtext: "First subscription only" },
                        { id: "EXISTING_ONLY", title: "Existing Customers Only", subtext: "Upsell or renewal campaigns" },
                        { id: "PARTNER_REFERRAL", title: "Partner / Reseller Referral", subtext: "Through accredited partners" },
                        { id: "COHORT", title: "Specific Campaign Cohort", subtext: "Explicit organization members" },
                        { id: "DOMAINS", title: "Specific Customer Domains", subtext: "Restricted corporate email domains" },
                      ].map((item) => (
                        <div key={item.id} className="col-12 col-sm-6">
                          <div
                            onClick={() => setCustomerEligibility(item.id)}
                            className={`selectable-card h-100 ${customerEligibility === item.id ? "selected" : ""}`}
                          >
                            <div className="card-title">{item.title}</div>
                            <div className="card-subtext">{item.subtext}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {customerEligibility === "DOMAINS" && (
                      <div className="mt-3 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label-custom mb-0">
                            Allowed Corporate Domains <span className="text-danger">*</span>
                          </label>
                          {eligibleDomains.length > 0 && (
                            <div className="d-flex align-items-center gap-2">
                              <span className="fs-sm text-secondary">
                                {eligibleDomains.length} {eligibleDomains.length === 1 ? "domain" : "domains"}
                              </span>
                              <Button size="small" color="error" onClick={handleClearAllDomains}>
                                Clear all
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="fs-sm text-secondary mb-2">
                          Users with email addresses matching these corporate domains will be eligible to redeem. Type a
                          domain and press <kbd>,</kbd> or <kbd>Enter</kbd> to add.
                        </div>

                        <div
                          onClick={() => domainInputRef.current?.focus()}
                          className={`tag-input ${isDomainFocused ? "is-focused" : ""}`}
                        >
                          {eligibleDomains.map((domain) => (
                            <Chip
                              key={domain}
                              size="small"
                              label={`@${domain}`}
                              onDelete={(e) => {
                                e.stopPropagation();
                                handleRemoveDomain(domain);
                              }}
                            />
                          ))}

                          <input
                            ref={domainInputRef}
                            type="text"
                            value={domainInput}
                            onChange={handleDomainInputChange}
                            onKeyDown={handleDomainKeyDown}
                            onBlur={handleDomainBlur}
                            onFocus={() => setIsDomainFocused(true)}
                            onPaste={handleDomainPaste}
                            placeholder={
                              eligibleDomains.length === 0 ? "e.g. dangoteagro.ng, acme.ng, bua.com" : "Add domain..."
                            }
                          />
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="fs-sm text-secondary">
                            Tip: You can paste a comma-separated list of domains to add them at once.
                          </span>
                          {domainInput.trim() && (
                            <Button size="small" variant="outlined" color="secondary" onClick={() => handleAddDomain(domainInput)}>
                              Add "{cleanDomain(domainInput)}"
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {customerEligibility === "COHORT" && (
                      <div className="mt-3 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <label className="form-label-custom mb-0">
                            Cohort Member Organizations <span className="text-danger">*</span>
                          </label>
                          {selectedCohortOrgs.length > 0 && (
                            <div className="d-flex align-items-center gap-2">
                              <span className="fs-sm text-secondary">{selectedCohortOrgs.length} selected</span>
                              <Button size="small" color="error" onClick={() => setSelectedCohortOrgs([])}>
                                Clear all
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="fs-sm text-secondary mb-2">
                          Search and select the organizations explicitly permitted to redeem this promotion.
                        </div>

                        {selectedCohortOrgs.length > 0 && (
                          <div className="d-flex flex-wrap gap-2 mb-2 p-2 border rounded bg-light" style={{ maxHeight: 110, overflowY: "auto" }}>
                            {selectedCohortOrgs.map((org) => (
                              <Chip
                                key={org.id}
                                size="small"
                                label={org.name}
                                title={org.name}
                                sx={{ maxWidth: 220 }}
                                onDelete={() => handleRemoveCohortOrg(org.id)}
                              />
                            ))}
                          </div>
                        )}

                        <div className="mb-2">
                          <AppTextField
                            value={orgSearchTerm}
                            onChange={(e) => setOrgSearchTerm(e.target.value)}
                            placeholder="Search organizations by name, email, or keyword..."
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search className="text-secondary" fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: orgSearchTerm ? (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setOrgSearchTerm("")} title="Clear search">
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        </div>

                        <div className="border rounded mb-3 bg-white" style={{ maxHeight: 220, overflowY: "auto" }}>
                          {isSearchingCohortOrgs ? (
                            <div className="p-3 text-center text-secondary fs-sm d-flex justify-content-center align-items-center gap-2">
                              <CircularProgress size={16} />
                              Searching organizations...
                            </div>
                          ) : availableCohortOrgs.length === 0 ? (
                            <div className="p-3 text-center text-secondary fs-sm">
                              {orgSearchTerm
                                ? `No organizations found matching "${orgSearchTerm}".`
                                : "No organizations available."}
                            </div>
                          ) : (
                            availableCohortOrgs.map((org) => {
                              const isSelected = selectedCohortOrgs.some((o) => o.id === org.id);
                              return (
                                <div
                                  key={org.id}
                                  onClick={() => handleToggleCohortOrg(org)}
                                  className={`cohort-org-row d-flex align-items-center justify-content-between px-2 py-1 border-bottom ${
                                    isSelected ? "is-selected" : ""
                                  }`}
                                >
                                  <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                                    <Checkbox size="small" color="secondary" checked={isSelected} tabIndex={-1} />
                                    <div className="text-truncate" style={{ minWidth: 0, flex: 1 }}>
                                      <div className="fw-medium text-truncate">{org.name}</div>
                                      <div className="fs-sm text-secondary text-truncate">
                                        {org.email ? `${org.email}` : org.id}
                                        {org.plan ? ` • ${org.plan}` : ""}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ms-2 flex-shrink-0">
                                    <PromoStatusBadge status={org.status || "ACTIVE"} />
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        <div className="mb-3">
                          <Button size="small" color="secondary" onClick={() => setShowManualOrgInput(!showManualOrgInput)}>
                            {showManualOrgInput ? "− Hide manual ID entry" : "+ Add organization by ID directly"}
                          </Button>
                          {showManualOrgInput && (
                            <div className="mt-2 d-flex gap-2">
                              <AppTextField
                                placeholder="Paste Organization ID (e.g. 66f0ab123...)"
                                value={manualOrgIdInput}
                                onChange={(e) => setManualOrgIdInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddManualOrgId();
                                  }
                                }}
                              />
                              <Button variant="outlined" color="secondary" className="flex-shrink-0" onClick={handleAddManualOrgId}>
                                Add ID
                              </Button>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="form-label-custom">
                            Cohort Reference Label <span className="text-secondary">(Optional audit tag)</span>
                          </label>
                          <AppTextField
                            value={cohortId}
                            onChange={(e) => setCohortId(e.target.value)}
                            placeholder="e.g. Q4 Lagos Tech Summit Attendees"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Minimum Spend (₦)</label>
                      <AppTextField
                        type="number"
                        value={minSpend}
                        onChange={(e) => setMinSpend(e.target.value)}
                        placeholder="e.g. 50000"
                      />
                    </div>
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Maximum Spend (₦)</label>
                      <AppTextField
                        type="number"
                        value={maxSpend}
                        onChange={(e) => setMaxSpend(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Min. Commitment</label>
                      <AppSelectField
                        value={minCommitment}
                        onChange={(e) => setMinCommitment(e.target.value)}
                        items={[
                          { label: "No minimum", value: "No minimum" },
                          { label: "Monthly", value: "MONTHLY" },
                          { label: "Quarterly (3 Months)", value: "QUARTERLY" },
                          { label: "Biannually (6 Months)", value: "BIANNUALLY" },
                          { label: "Annually (1 Year)", value: "ANNUALLY" },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ================= STAGE 3: Steps 5 & 6 ================= */}
            {currentStage === 3 && (
              <div>
                {/* Step 5: Usage Limits */}
                <div className="mb-4">
                  {renderStepTitle(5, "Usage Limits")}

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Total Redemption Limit</label>
                      <AppTextField
                        type="number"
                        value={totalRedemptionLimit}
                        onChange={(e) => setTotalRedemptionLimit(e.target.value)}
                        placeholder="Optional limit (e.g. 500)"
                        helperText="Total uses across all clients"
                      />
                    </div>
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Limit Per Customer</label>
                      <AppSelectField
                        value={limitPerCustomer}
                        onChange={(e) => setLimitPerCustomer(e.target.value)}
                        items={[
                          { label: "Once per customer", value: "1" },
                          { label: "Twice per customer", value: "2" },
                          ...(!["1", "2", "UNLIMITED"].includes(limitPerCustomer)
                            ? [{ label: `${limitPerCustomer} times per customer`, value: limitPerCustomer }]
                            : []),
                          { label: "Unlimited per customer", value: "UNLIMITED" },
                        ]}
                      />
                    </div>
                    <div className="col-12 col-sm-4">
                      <label className="form-label-custom">Max Redemptions / Day</label>
                      <AppTextField
                        type="number"
                        value={maxPerDay}
                        onChange={(e) => setMaxPerDay(e.target.value)}
                        placeholder="e.g. 50 (Velocity control)"
                      />
                    </div>
                  </div>

                  <div className="p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2 fs-sm">
                      <span className="text-secondary">Usage Indicator Preview</span>
                      <span className="fw-medium">
                        0 / {totalRedemptionLimit ? `${Number(totalRedemptionLimit).toLocaleString()} redeemed` : "Unlimited"}
                      </span>
                    </div>
                    <LinearProgress variant="determinate" value={0} color="secondary" sx={{ borderRadius: 10, height: 6 }} />
                    <div className="fs-sm text-secondary mt-1">
                      Visual burn-rate indicator displayed across dashboard tables and reports.
                    </div>
                  </div>
                </div>

                <Divider className="my-4" />

                {/* Step 6: Validity & Scheduling */}
                <div>
                  {renderStepTitle(6, "Validity & Scheduling")}

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <div className="row g-3 mb-2">
                      {renderDateTimeField({
                        label: "Start Date & Time (Africa/Lagos WAT)",
                        required: true,
                        date: startDate,
                        onDateChange: (newDate) => setStartDate(newDate),
                        time: startTime,
                        onTimeChange: (newTime) => setStartTime(newTime),
                        minDate: isEditMode ? undefined : new Date(),
                      })}
                      {renderDateTimeField({
                        label: "Expiry Date & Time",
                        date: expiryDate,
                        onDateChange: (newDate) => setExpiryDate(newDate),
                        time: expiryTime,
                        onTimeChange: (newTime) => setExpiryTime(newTime),
                        minDate: startDate,
                        disabled: neverExpires,
                      })}
                    </div>
                  </LocalizationProvider>

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={neverExpires}
                        onChange={(e) => setNeverExpires(e.target.checked)}
                      />
                    }
                    label="Never expires"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-top d-flex justify-content-between align-items-center gap-3">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                if (currentStage > 1) {
                  setCurrentStage((prev) => prev - 1);
                } else {
                  navigate(pathnames.PROMO_CODES_OVERVIEW);
                }
              }}
            >
              {currentStage === 1 ? "Cancel" : "Previous Stage"}
            </Button>

            {currentStage < 3 ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  if (validateStage(currentStage)) {
                    setCurrentStage((prev) => prev + 1);
                  }
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={<BTNLoader isLoading={isSubmitting} size={16} color="inherit" />}
              >
                {isEditMode ? "Save Changes" : "Publish Promo Code"}
              </Button>
            )}
          </div>
        </Paper>
      </div>
    </AppLayout>
  );
}
